import { QUESTION_PICK_NONE } from 'lib/generated_constants/surveys';
import { type Option } from 'lib/participant_search/filters/types';

import { SurveyRawShowResponse } from 'api/project_surveys/endpoints';

import { type RawAnswer, type RawQuestion } from './types';

// Parses questions from a project survey response that are multi-select or single-select.
// Does not include text-based questions or draft questions as they are incomplete
export function parseProjectSurveyQuestions(
  projectSurvey?: SurveyRawShowResponse,
): RawQuestion[] {
  if (!projectSurvey?.included) return [];

  return projectSurvey.included.filter(
    (item) =>
      item.type === 'question' &&
      'pick' in item.attributes &&
      item.attributes.pick !== QUESTION_PICK_NONE &&
      item.id,
  ) as RawQuestion[];
}

// Creates a map of question IDs to their corresponding answer options.
export function createSurveyQuestiontoAnswerOptionsMap(
  projectSurvey: SurveyRawShowResponse | undefined,
  questions: RawQuestion[],
): Map<string, Option[]> {
  if (!projectSurvey?.included || questions.length === 0) {
    return new Map();
  }

  const answersById = new Map(
    (
      projectSurvey.included.filter(
        (item) => item.type === 'answer',
      ) as RawAnswer[]
    ).map((answer) => [answer.id, answer]),
  );

  return questions.reduce<Map<string, Option[]>>((acc, question) => {
    const answers = question.relationships?.answers?.data;

    if (answers && answers.length > 0) {
      const transformedAnswers = answers
        .map((answerRef) => {
          const answer = answersById.get(answerRef.id);
          if (!answer) return null;

          return {
            label: answer.attributes.text,
            value: answer.id,
          };
        })
        .filter((option) => option !== null) as Option[];

      acc.set(question.id, transformedAnswers);
    }
    return acc;
  }, new Map());
}

// Creates a reverse map from answer IDs to their parent question IDs.
// Used when hydrating popover form values from server response that only contains answer IDs.
export function createAnswerToSurveyQuestionMap(
  questions: RawQuestion[],
): Map<string, string> {
  return questions.reduce<Map<string, string>>((acc, question) => {
    const answers = question.relationships?.answers?.data;

    if (answers && answers.length > 0) {
      answers.forEach((answerRef) => {
        acc.set(answerRef.id, question.id);
      });
    }
    return acc;
  }, new Map());
}
